import { Phone, MapPin, BadgeCheck, ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { COMPANY, PHONE_DISPLAY, PHONE_LINK, WA_LINK, ADDRESS_LINES, DIRECTIONS_LINK, GSTIN } from "../data/site";

const CARDS = [
  {
    icon: Phone,
    label: "Call Us",
    value: PHONE_DISPLAY,
    sub: "Mon–Sat, business hours",
    href: PHONE_LINK,
    testid: "contact-page-call-card",
  },
  {
    icon: MapPin,
    label: "Factory Address",
    value: ADDRESS_LINES.join(" "),
    sub: "Get Directions",
    href: DIRECTIONS_LINK,
    testid: "contact-page-address-card",
  },
  {
    icon: BadgeCheck,
    label: "GSTIN",
    value: GSTIN,
    sub: COMPANY,
    testid: "contact-page-gstin-card",
  },
];

export default function Contact() {
  return (
    <main>
      <PageHero
        kicker="Contact / Get a Quote"
        title="Talk to MODORS"
        desc="Call, WhatsApp or send the form — we respond with product details and a quotation for your project."
      />
      <section className="bg-white py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid sm:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {CARDS.map((c, i) => {
              const Inner = (
                <>
                  <c.icon className="w-6 h-6 text-brand-red" strokeWidth={1.75} />
                  <div className="mt-5 text-[10px] font-semibold tracking-[0.28em] uppercase text-brand-steel">
                    {c.label}
                  </div>
                  <div className="mt-2 font-display text-lg font-semibold text-brand-ink leading-snug break-words">
                    {c.value}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.15em] uppercase text-brand-red">
                    {c.sub} {c.href && <ArrowUpRight className="w-3.5 h-3.5" />}
                  </div>
                </>
              );
              return (
                <Reveal key={c.label} delay={i * 0.08} className="bg-white">
                  {c.href ? (
                    <a
                      href={c.href}
                      target={c.href.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      data-testid={c.testid}
                      className="block h-full p-7 hover:bg-brand-mist transition-colors duration-300"
                    >
                      {Inner}
                    </a>
                  ) : (
                    <div data-testid={c.testid} className="h-full p-7">{Inner}</div>
                  )}
                </Reveal>
              );
            })}
          </div>

          <div className="mt-14 grid lg:grid-cols-12 gap-6 items-stretch">
            <Reveal className="lg:col-span-7">
              <QuoteForm title="REQUEST A QUOTE" />
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-5">
              <div className="h-full bg-brand-coal p-8 sm:p-10 relative overflow-hidden flex flex-col">
                <div className="absolute inset-0 noise-overlay opacity-[0.06]" />
                <div className="relative flex-1 flex flex-col">
                  <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-brand-red">
                    Prefer WhatsApp?
                  </div>
                  <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold uppercase tracking-tight text-white leading-tight">
                    Send Your Requirement Directly
                  </h3>
                  <p className="mt-4 text-sm text-white/55 leading-relaxed">
                    Message us your sizes, quantities and site location — get pricing and
                    availability straight on WhatsApp.
                  </p>
                  <div className="mt-8 space-y-3">
                    <a
                      href={WA_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-page-whatsapp-btn"
                      className="inline-flex w-full items-center justify-center gap-2 bg-[#25D366] text-white text-sm font-semibold tracking-[0.14em] uppercase px-6 py-4 hover:brightness-110 transition-[filter] duration-300"
                    >
                      WhatsApp MODORS
                    </a>
                    <a
                      href={PHONE_LINK}
                      data-testid="contact-page-call-btn"
                      className="inline-flex w-full items-center justify-center gap-2 border border-white/30 text-white text-sm font-semibold tracking-[0.14em] uppercase px-6 py-4 hover:border-brand-red hover:text-brand-red transition-colors duration-300"
                    >
                      <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
                    </a>
                  </div>
                  <div className="mt-auto pt-10">
                    <div className="border-t border-white/10 pt-6">
                      <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-white/40">
                        Manufactured by
                      </div>
                      <div className="mt-1.5 font-display text-base font-semibold uppercase text-white">
                        {COMPANY}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
